<template>
  <div class="flex w-full flex-col md:flex-row md:space-x-2">
    <div class="w-full md:w-1/2">
      <StateAutocomplete
        ref="stateAutocomplete"
        :initial-state="props.initialState"
        :states-cities="props.statesCities"
        :ssr-mode="ssrMode"
        @update:selected="handleStateSelect"
        @update:input="handleStateInput"
        @update:exact-match="handleStateExactMatch"
      />
    </div>
    <div class="mt-2 w-full md:mt-0 md:w-1/2">
      <CityAutocomplete
        ref="cityAutocomplete"
        :state-code="state?.code"
        :state-name="state?.label"
        :initial-city="props.initialCity"
        :disabled="!hasValidState || citiesLoading"
        :pre-fetched-cities="citiesForState"
        :ssr-mode="ssrMode"
        @update:selected="handleCitySelect"
        @update:input="handleCityInput"
        @update:exact-match="handleCityExactMatch"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'
import StateAutocomplete from './StateAutocomplete.vue'
import CityAutocomplete, { type City } from './CityAutocomplete.vue'
import { stateOptions } from '~/utils/states'

const props = defineProps<{
  initialState?: string
  initialCity?: string
  preFetchedCities?: City[]
  statesCities?: Record<string, City[]>
  permalink?: string
  section?: 'salaries' | 'reviews'
  ssrMode?: boolean
}>()
const emit = defineEmits<{
  'update:state': [any]
  'update:state-input': [string]
  'update:city': [any]
  'update:city-input': [string]
  'update:stateExactMatch': [boolean]
  'update:cityExactMatch': [boolean]
}>()
const initialStateOption = computed(() => {
  if (props.initialState) {
    return stateOptions.find(state => state.code === props.initialState)
  }
  return null
})
const state = ref<{ code: string, label: string } | null>(
  initialStateOption.value
)
const stateInput = ref(initialStateOption.value?.label || '')
const stateExactMatch = ref(!!initialStateOption.value)
const citiesForState = ref<City[]>([])
const citiesLoading = ref(false)
const citiesCache = ref<Record<string, City[]>>({})
const hasValidState = computed(() => {
  if (state.value) return true
  if (stateInput.value && !stateExactMatch.value) {
    const normalizedInput = stateInput.value.toLowerCase().trim()
    return !!stateOptions.find(
      s =>
        s.label.toLowerCase().trim() === normalizedInput
        || s.code.toLowerCase() === normalizedInput
    )
  }
  if (props.initialState && !state.value) {
    return !!stateOptions.find(s => s.code === props.initialState)
  }
  return false
})
const ssrMode = computed(() => {
  return props.ssrMode !== undefined ? props.ssrMode : import.meta.server
})
function handleStateSelect(selectedState: any) {
  state.value = selectedState
  stateInput.value = selectedState?.label || ''
  emit('update:state', selectedState)
  if (selectedState) {
    updateCitiesForState(selectedState.label)
  } else {
    citiesForState.value = []
  }
}
function updateCitiesForState(stateKey: string) {
  if (!stateKey) {
    citiesForState.value = []
    return
  }
  citiesLoading.value = true
  if (citiesCache.value[stateKey]) {
    citiesForState.value = citiesCache.value[stateKey]
    citiesLoading.value = false
    return
  }
  if (props.statesCities && props.statesCities[stateKey]) {
    const cities = props.statesCities[stateKey].map(city =>
      typeof city === 'string' ? { name: city } : city
    )
    citiesForState.value = cities
    citiesCache.value[stateKey] = cities
    citiesLoading.value = false
    return
  }
  if (props.preFetchedCities && props.preFetchedCities.length > 0) {
    citiesForState.value = props.preFetchedCities
    citiesCache.value[stateKey] = props.preFetchedCities
  }
  citiesLoading.value = false
}
function handleStateInput(value: string) {
  stateInput.value = value
  emit('update:state-input', value)
  if (!value) {
    state.value = null
    citiesForState.value = []
  }
}
function handleStateExactMatch(hasMatch: boolean) {
  stateExactMatch.value = hasMatch
  emit('update:stateExactMatch', hasMatch)
}
function handleCitySelect(selectedCity: any) {
  emit('update:city', selectedCity)
}
function handleCityInput(value: string) {
  emit('update:city-input', value)
}
function handleCityExactMatch(hasMatch: boolean) {
  emit('update:cityExactMatch', hasMatch)
}
onMounted(() => {
  if (!ssrMode.value) {
    if (props.initialState && !state.value) {
      const stateOption = stateOptions.find(
        state => state.code === props.initialState
      )
      if (stateOption) {
        state.value = stateOption
        stateInput.value = stateOption.label
        stateExactMatch.value = true
      }
    }
    if (props.statesCities) {
      Object.entries(props.statesCities).forEach(([stateName, cities]) => {
        citiesCache.value[stateName] = cities.map(city =>
          typeof city === 'string' ? { name: city } : city
        )
      })
    }
    if (props.initialState) {
      const stateOption = stateOptions.find(
        state => state.code === props.initialState
      )
      if (stateOption) {
        updateCitiesForState(stateOption.label)
      } else {
        updateCitiesForState(props.initialState)
      }
    }
  }
  if (props.initialState && !state.value) {
    const stateOption = stateOptions.find(
      state => state.code === props.initialState
    )
    if (stateOption) {
      state.value = stateOption
      stateInput.value = stateOption.label
      stateExactMatch.value = true
    }
  }
  if (props.statesCities) {
    Object.entries(props.statesCities).forEach(([stateName, cities]) => {
      citiesCache.value[stateName] = cities.map(city =>
        typeof city === 'string' ? { name: city } : city
      )
    })
  }
  if (props.initialState) {
    const stateOption = stateOptions.find(
      state => state.code === props.initialState
    )
    if (stateOption) {
      updateCitiesForState(stateOption.label)
    } else {
      updateCitiesForState(props.initialState)
    }
  }
})
watch(
  () => props.initialState,
  (newInitialState) => {
    if (
      newInitialState
      && (!state.value || state.value.code !== newInitialState)
    ) {
      const stateOption = stateOptions.find(
        state => state.code === newInitialState
      )
      if (stateOption) {
        state.value = stateOption
        stateInput.value = stateOption.label
        stateExactMatch.value = true
        updateCitiesForState(stateOption.label)
      }
    }
  },
  { immediate: true }
)
watch(
  () => props.statesCities,
  (newStatesCities) => {
    if (newStatesCities) {
      Object.entries(newStatesCities).forEach(([stateName, cities]) => {
        const formattedCities = cities.map(city =>
          typeof city === 'string' ? { name: city } : city
        )
        citiesCache.value[stateName] = formattedCities
      })
      if (state.value) {
        updateCitiesForState(state.value.code || state.value.label)
      }
    }
  },
  { deep: true }
)
watch(
  () => props.preFetchedCities,
  (newCities) => {
    if (newCities && newCities.length > 0 && state.value) {
      citiesForState.value = newCities
      citiesCache.value[state.value.code || state.value.label] = newCities
    }
  },
  { immediate: true }
)
</script>
